// Prefer same-origin API by default (works when the page is served by Laravel)
// Optionally override via Vite env: VITE_API_BASE_URL (e.g., "http://localhost:8000")
const API_ORIGIN = (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_API_BASE_URL) || '';
const API_BASE_PATH = '/api';
const buildUrl = (endpoint) => `${API_ORIGIN}${API_BASE_PATH}${endpoint}`;

class ApiService {
	constructor() {
		this.token = localStorage.getItem('auth_token');
	}

	setToken(token) {
		this.token = token;
		localStorage.setItem('auth_token', token);
	}

	removeToken() {
		this.token = null;
		localStorage.removeItem('auth_token');
	}

	getHeaders() {
		const headers = {
			'Content-Type': 'application/json',
			'Accept': 'application/json',
		};

		if (this.token) {
			headers['Authorization'] = `Bearer ${this.token}`;
		}

		return headers;
	}

	async request(endpoint, options = {}) {
		const url = buildUrl(endpoint);
		const config = {
			headers: this.getHeaders(),
			credentials: 'include', // Include cookies for CSRF protection
			...options,
		};

		try {
			const response = await fetch(url, config);
			
			// Handle 401 Unauthorized by clearing token and redirecting to login
			if (response.status === 401) {
				this.removeToken();
				// Don't throw error for public endpoints
				if (!endpoint.startsWith('/login') && !endpoint.startsWith('/register')) {
					console.warn('Authentication required for:', endpoint);
					// For admin endpoints, redirect to login
					if (endpoint.startsWith('/admin/')) {
						window.location.href = '/login?redirect=' + encodeURIComponent(window.location.pathname);
						return;
					}
				}
			}

			// Handle 403 Forbidden - user doesn't have permission
			if (response.status === 403) {
				console.warn('Access denied for:', endpoint);
				// Don't clear token for 403, just log the warning
			}

			let data;
			try {
				data = await response.json();
			} catch (_) {
				data = {};
			}

			if (!response.ok) {
				throw new Error(data.message || `HTTP ${response.status}: ${response.statusText}`);
			}

			return data;
		} catch (error) {
			// Soften noise for expected failures (e.g., wishlist for guests)
			if (endpoint === '/wishlist' || endpoint === '/vendor-wishlist') {
				return { success: true, data: [] };
			}
			console.error('API Error:', error);
			throw error;
		}
	}

	// Auth endpoints
	async login(email, password) {
		return this.request('/login', {
			method: 'POST',
			body: JSON.stringify({ email, password }),
		});
	}

	async register(userData) {
		return this.request('/register', {
			method: 'POST',
			body: JSON.stringify(userData),
		});
	}

	async logout() {
		const result = await this.request('/logout', {
			method: 'POST',
		});
		this.removeToken();
		return result;
	}

	async getProfile() {
		return this.request('/me');
	}

	async updateProfile(profileData) {
		return this.request('/profile', {
			method: 'PUT',
			body: JSON.stringify(profileData),
		});
	}

	// Vendor endpoints
	async getVendors(params = {}) {
		const queryString = new URLSearchParams(params).toString();
		return this.request(`/vendors${queryString ? `?${queryString}` : ''}`);
	}

	async getVendor(id) {
		return this.request(`/vendors/${id}`);
	}

	async getCities() {
		return this.request('/vendors/cities');
	}

	// Meta for Get Started wizard
	async getGetStartedMeta() {
		return this.request('/meta/get-started');
	}

	// Quotes
	async createQuote(payload) {
		return this.request('/quotes', { method: 'POST', body: JSON.stringify(payload) });
	}
	async getQuotes() {
		return this.request('/quotes');
	}

	async getQuote(id) {
		return this.request(`/quotes/${id}`);
	}

	async acceptQuote(id, customerNotes = '') {
		return this.request(`/quotes/${id}/accept`, {
			method: 'POST',
			body: JSON.stringify({ customer_notes: customerNotes })
		});
	}

	async rejectQuote(id, customerNotes = '') {
		return this.request(`/quotes/${id}/reject`, {
			method: 'POST',
			body: JSON.stringify({ customer_notes: customerNotes })
		});
	}

	// Activities
	async getActivities(params = {}) {
		const queryString = new URLSearchParams(params).toString();
		return this.request(`/activities${queryString ? '?' + queryString : ''}`);
	}

	async getActivityStats(params = {}) {
		const queryString = new URLSearchParams(params).toString();
		return this.request(`/activities/stats${queryString ? '?' + queryString : ''}`);
	}

	async getActivitiesByType(type, params = {}) {
		const queryString = new URLSearchParams(params).toString();
		return this.request(`/activities/type/${type}${queryString ? '?' + queryString : ''}`);
	}

	// Preferences
	async getPreferences() {
		return this.request('/preferences');
	}

	async updatePreferences(preferences) {
		return this.request('/preferences', {
			method: 'PUT',
			body: JSON.stringify(preferences)
		});
	}

	async updateNotificationPreferences(notificationPreferences) {
		return this.request('/preferences/notifications', {
			method: 'PUT',
			body: JSON.stringify(notificationPreferences)
		});
	}

	async resetPreferences(type = 'all') {
		return this.request('/preferences/reset', {
			method: 'POST',
			body: JSON.stringify({ type })
		});
	}

	async getPreference(key) {
		return this.request(`/preferences/${key}`);
	}

	async setPreference(key, value) {
		return this.request(`/preferences/${key}`, {
			method: 'PUT',
			body: JSON.stringify({ value })
		});
	}

	// Advanced Search
	async getSearchSuggestions(type) {
		return this.request(`/search/suggestions/${type}`);
	}

	async getPopularSearches(type) {
		return this.request(`/search/popular/${type}`);
	}

	async advancedSearch(params) {
		return this.request('/search/advanced', {
			method: 'POST',
			body: JSON.stringify(params)
		});
	}

	// Favorites
	async getFavorites(type = null, category = null) {
		const params = new URLSearchParams();
		if (type) params.append('type', type);
		if (category) params.append('category', category);
		return this.request(`/favorites${params.toString() ? '?' + params.toString() : ''}`);
	}

	async toggleFavorite(type, id, category = null, metadata = null) {
		return this.request('/favorites/toggle', {
			method: 'POST',
			body: JSON.stringify({ type, id, category, metadata })
		});
	}

	async addFavorite(type, id, category = null, metadata = null) {
		return this.request('/favorites', {
			method: 'POST',
			body: JSON.stringify({ type, id, category, metadata })
		});
	}

	async removeFavorite(type, id) {
		return this.request('/favorites', {
			method: 'DELETE',
			body: JSON.stringify({ type, id })
		});
	}

	// Recommendations
	async getRecommendations(params = {}) {
		const queryString = new URLSearchParams(params).toString();
		return this.request(`/recommendations${queryString ? '?' + queryString : ''}`);
	}

	// Social Features
	async getReviews(params = {}) {
		const queryString = new URLSearchParams(params).toString();
		return this.request(`/reviews${queryString ? '?' + queryString : ''}`);
	}

	async getReviewStats(params = {}) {
		const queryString = new URLSearchParams(params).toString();
		return this.request(`/reviews/stats${queryString ? '?' + queryString : ''}`);
	}

	async submitReview(data) {
		return this.request('/reviews', {
			method: 'POST',
			body: JSON.stringify(data)
		});
	}

	async likeReview(reviewId) {
		return this.request(`/reviews/${reviewId}/like`, {
			method: 'POST'
		});
	}

	async shareReview(reviewId) {
		return this.request(`/reviews/${reviewId}/share`, {
			method: 'POST'
		});
	}

	// Analytics
	async getUserAnalytics(params = {}) {
		const queryString = new URLSearchParams(params).toString();
		return this.request(`/analytics/user${queryString ? '?' + queryString : ''}`);
	}

	// Gamification
	async getGamificationData() {
		return this.request('/gamification');
	}

	async claimReward(rewardId) {
		return this.request(`/gamification/rewards/${rewardId}/claim`, {
			method: 'POST'
		});
	}

	// Calendar
	async getCalendarEvents(params = {}) {
		const queryString = new URLSearchParams(params).toString();
		return this.request(`/calendar/events${queryString ? '?' + queryString : ''}`);
	}

	async createCalendarEvent(data) {
		return this.request('/calendar/events', {
			method: 'POST',
			body: JSON.stringify(data)
		});
	}

	async updateCalendarEvent(eventId, data) {
		return this.request(`/calendar/events/${eventId}`, {
			method: 'PUT',
			body: JSON.stringify(data)
		});
	}

	async deleteCalendarEvent(eventId) {
		return this.request(`/calendar/events/${eventId}`, {
			method: 'DELETE'
		});
	}

	// PWA Features
	async getPWAStatus() {
		return this.request('/pwa/status');
	}

	async updatePWASettings(settings) {
		return this.request('/pwa/settings', {
			method: 'PUT',
			body: JSON.stringify(settings)
		});
	}

	async getCategories() {
		// Try primary endpoint, then common fallbacks. Return an array on success or [] on failure.
		try {
			const res = await this.request('/vendors/categories');
			return Array.isArray(res) ? res : (Array.isArray(res?.data) ? res.data : []);
		} catch (_) {
			try {
				const res2 = await this.request('/categories');
				return Array.isArray(res2) ? res2 : (Array.isArray(res2?.data) ? res2.data : []);
			} catch (e2) {
				console.warn('Falling back to empty categories due to API error');
				return [];
			}
		}
	}

	async createCategory(data) {
		return this.request('/categories', {
			method: 'POST',
			body: JSON.stringify(data)
		});
	}

	async updateCategory(id, data) {
		return this.request(`/categories/${id}`, {
			method: 'PUT',
			body: JSON.stringify(data)
		});
	}

	async createCategoryWithFile(formData) {
		const url = buildUrl('/categories');
		const headers = {};
		if (this.token) headers['Authorization'] = `Bearer ${this.token}`;
		const res = await fetch(url, { method: 'POST', headers, body: formData });
		const data = await res.json();
		if (!res.ok) throw new Error(data.message || 'Create failed');
		return data;
	}

	async updateCategoryWithFile(id, formData) {
		const url = buildUrl(`/categories/${id}`);
		const headers = {};
		if (this.token) headers['Authorization'] = `Bearer ${this.token}`;
		const res = await fetch(url, { method: 'PUT', headers, body: formData });
		const data = await res.json();
		if (!res.ok) throw new Error(data.message || 'Update failed');
		return data;
	}

	async deleteCategory(id) {
		return this.request(`/categories/${id}`, {
			method: 'DELETE'
		});
	}

	// Vendor self profile endpoints
	async getMyVendor() {
		return this.request('/vendor/me');
	}

	async saveMyVendor(formData) {
		return this.request('/vendor/me', {
			method: 'POST',
			body: JSON.stringify(formData),
		});
	}

	async uploadMyVendorImage(file, meta = {}) {
		const url = buildUrl('/vendor/me/images');
		const form = new FormData();
		form.append('image', file);
		if (meta.type) form.append('type', meta.type);
		if (meta.alt_text) form.append('alt_text', meta.alt_text);
		if (typeof meta.is_primary !== 'undefined') form.append('is_primary', meta.is_primary ? '1' : '0');

		const headers = {};
		if (this.token) headers['Authorization'] = `Bearer ${this.token}`;

		const res = await fetch(url, { method: 'POST', headers, body: form });
		const data = await res.json();
		if (!res.ok) throw new Error(data.message || 'Upload failed');
		return data;
	}

	async uploadVendorMedia(file, category, meta = {}) {
		const url = buildUrl('/vendor/me/media');
		const form = new FormData();
		form.append('file', file);
		form.append('category', category);
		if (meta.alt_text) form.append('alt_text', meta.alt_text);
		if (meta.description) form.append('description', meta.description);

		const headers = {};
		if (this.token) headers['Authorization'] = `Bearer ${this.token}`;

		const res = await fetch(url, { method: 'POST', headers, body: form });
		const data = await res.json();
		if (!res.ok) throw new Error(data.message || 'Upload failed');
		return data;
	}

	async uploadVendorMenuImage(file, meta = {}) {
		const url = buildUrl('/vendor/me/menu-image');
		const form = new FormData();
		form.append('image', file);
		if (meta.alt_text) form.append('alt_text', meta.alt_text);

		const headers = {};
		if (this.token) headers['Authorization'] = `Bearer ${this.token}`;

		const res = await fetch(url, { method: 'POST', headers, body: form });
		const data = await res.json();
		if (!res.ok) throw new Error(data.message || 'Upload failed');
		return data;
	}

	// Menu endpoints
	async getMenus(params = {}) {
		const queryString = new URLSearchParams(params).toString();
		return this.request(`/menus${queryString ? `?${queryString}` : ''}`);
	}

	async getMenu(id) {
		return this.request(`/menus/${id}`);
	}

	async getVendorMenus(vendorId) {
		return this.request(`/vendors/${vendorId}/menus`);
	}

	async createMenu(menu) {
		// Handle both FormData and regular objects
		let form;
		if (menu instanceof FormData) {
			form = menu;
		} else {
			form = new FormData();
			Object.entries(menu).forEach(([k, v]) => {
				if (v === undefined || v === null) return;
				if (k === 'image' && v instanceof File) {
					form.append('image', v);
				} else if (k === 'images' && Array.isArray(v)) {
					// Handle multiple images
					v.forEach((imageData, index) => {
						if (imageData && imageData.file) {
							form.append(`images[${index}]`, imageData.file);
						}
					});
				} else if (Array.isArray(v)) {
					form.append(k, JSON.stringify(v));
				} else {
					form.append(k, v);
				}
			});
		}

		const url = buildUrl('/menus');
		const headers = {};
		if (this.token) headers['Authorization'] = `Bearer ${this.token}`;
		const res = await fetch(url, { method: 'POST', headers, body: form });
		const data = await res.json();
		if (!res.ok) throw new Error(data.message || 'Create failed');
		return data;
	}

	async updateMenu(id, menu) {
		// Handle both FormData and regular objects
		let form;
		if (menu instanceof FormData) {
			form = menu;
		} else {
			form = new FormData();
			Object.entries(menu).forEach(([k, v]) => {
				if (v === undefined || v === null) return;
				if (k === 'image' && v instanceof File) {
					form.append('image', v);
				} else if (k === 'images' && Array.isArray(v)) {
					// Handle multiple images
					v.forEach((imageData, index) => {
						if (imageData && imageData.file) {
							form.append(`images[${index}]`, imageData.file);
						}
					});
				} else if (Array.isArray(v)) {
					form.append(k, JSON.stringify(v));
				} else {
					form.append(k, v);
				}
			});
		}

		const url = buildUrl(`/menus/${id}`);
		const headers = {};
		if (this.token) headers['Authorization'] = `Bearer ${this.token}`;
		const res = await fetch(url, { method: 'POST', headers, body: form }); // Use POST for FormData
		const data = await res.json();
		if (!res.ok) throw new Error(data.message || 'Update failed');
		return data;
	}

	async createMenuWithFile(formData) {
		const url = buildUrl('/menus');
		const headers = {};
		if (this.token) headers['Authorization'] = `Bearer ${this.token}`;
		const res = await fetch(url, { method: 'POST', headers, body: formData });
		const data = await res.json();
		if (!res.ok) throw new Error(data.message || 'Create failed');
		return data;
	}

	async updateMenuWithFile(id, formData) {
		// Split update into two calls to avoid WAF blocking multipart with binary data
		const hasFile = formData.has('image') && formData.get('image').size > 0;
		
		// Step 1: Update non-file fields using POST with _method=PUT
		const fieldsData = new FormData();
		fieldsData.append('_method', 'PUT');
		
		// Copy all non-file fields, ensuring proper boolean values
		for (const [key, value] of formData.entries()) {
			if (key !== 'image') {
				// Handle boolean fields properly
				if (['is_vegetarian', 'is_vegan', 'is_gluten_free', 'is_spicy', 'is_available'].includes(key)) {
					// Convert to proper boolean string that Laravel accepts
					if (value === 'true' || value === true || value === '1' || value === 1) {
						fieldsData.append(key, '1');
					} else if (value === 'false' || value === false || value === '0' || value === 0) {
						fieldsData.append(key, '0');
					} else {
						// Default to false for empty/invalid values
						fieldsData.append(key, '0');
					}
				} else {
					fieldsData.append(key, value);
				}
			}
		}
		
		const fieldsUrl = buildUrl(`/menus/${id}`);
		const headers = { 'Accept': 'application/json' };
		if (this.token) headers['Authorization'] = `Bearer ${this.token}`;
		
		const fieldsRes = await fetch(fieldsUrl, { method: 'POST', headers, body: fieldsData });
		let fieldsRaw;
		try { fieldsRaw = await fieldsRes.text(); } catch (_) { fieldsRaw = ''; }
		let fieldsResponse;
		try { fieldsResponse = fieldsRaw ? JSON.parse(fieldsRaw) : {}; } catch (_) { fieldsResponse = {}; }
		
		if (!fieldsRes.ok) {
			const msg = fieldsResponse && fieldsResponse.message ? fieldsResponse.message : (`${fieldsRes.status} ${fieldsRes.statusText}`);
			throw new Error(msg);
		}
		
		// Step 2: Update image separately if present
		if (hasFile) {
			const imageData = new FormData();
			imageData.append('image', formData.get('image'));
			
			const imageUrl = buildUrl(`/menus/${id}/upload-image`);
			const imageRes = await fetch(imageUrl, { method: 'POST', headers, body: imageData });
			let imageRaw;
			try { imageRaw = await imageRes.text(); } catch (_) { imageRaw = ''; }
			let imageResponse;
			try { imageResponse = imageRaw ? JSON.parse(imageRaw) : {}; } catch (_) { imageResponse = {}; }
			
			if (!imageRes.ok) {
				const msg = imageResponse && imageResponse.message ? imageResponse.message : (`${imageRes.status} ${imageRes.statusText}`);
				console.error('Image update failed:', msg, imageResponse);
				throw new Error(`Image update failed: ${msg}`);
			}
			
			// Merge image data into the response
			if (imageResponse.data && imageResponse.data.image) {
				fieldsResponse.data.image = imageResponse.data.image;
				fieldsResponse.data.imageVariants = imageResponse.data.imageVariants;
			}
		}
		
		return fieldsResponse;
	}

	async deleteMenu(id) {
		return this.request(`/menus/${id}`, { method: 'DELETE' });
	}

	// Booking endpoints
	async getBookings(params = {}) {
		const queryString = new URLSearchParams(params).toString();
		return this.request(`/bookings${queryString ? `?${queryString}` : ''}`);
	}

	async getBooking(id) {
		return this.request(`/bookings/${id}`);
	}

	async createBooking(bookingData) {
		return this.request('/bookings', {
			method: 'POST',
			body: JSON.stringify(bookingData),
		});
	}

	async createBookingFromGetStarted(getStartedData) {
		return this.request('/bookings/get-started', {
			method: 'POST',
			body: JSON.stringify(getStartedData),
		});
	}

	async updateBookingStatus(id, status) {
		return this.request(`/bookings/${id}/status`, {
			method: 'PUT',
			body: JSON.stringify({ status }),
		});
	}

	async cancelBooking(id) {
		return this.request(`/bookings/${id}/cancel`, {
			method: 'PUT',
		});
	}

	async getBookingStats() {
		return this.request('/bookings/stats');
	}

	async payBooking(id) {
		return this.request(`/bookings/${id}/pay`, { method: 'POST' });
	}

	// Booking Draft endpoints
	async getBookingDraft() {
		return this.request('/bookings/draft');
	}

	async createBookingDraft(draftData) {
		return this.request('/bookings/draft', {
			method: 'POST',
			body: JSON.stringify(draftData),
		});
	}

	async updateBookingDraft(id, draftData) {
		return this.request(`/bookings/draft/${id}`, {
			method: 'PUT',
			body: JSON.stringify(draftData),
		});
	}

	async deleteBookingDraft(id) {
		return this.request(`/bookings/draft/${id}`, {
			method: 'DELETE',
		});
	}

	// Lead endpoints
	async getLeads(params = {}) {
		const queryString = new URLSearchParams(params).toString();
		return this.request(`/leads${queryString ? `?${queryString}` : ''}`);
	}

	async getMyLeads(params = {}) {
		const queryString = new URLSearchParams(params).toString();
		return this.request(`/leads/my${queryString ? `?${queryString}` : ''}`);
	}

	async getVendorLeads(params = {}) {
		const queryString = new URLSearchParams(params).toString();
		return this.request(`/vendor/leads${queryString ? `?${queryString}` : ''}`);
	}

	async getLead(id) {
		return this.request(`/leads/${id}`);
	}

	async createLead(leadData) {
		return this.request('/leads', {
			method: 'POST',
			body: JSON.stringify(leadData),
		});
	}

	async updateLead(id, leadData) {
		return this.request(`/leads/${id}`, {
			method: 'PUT',
			body: JSON.stringify(leadData),
		});
	}

	async calculateAutoQuantities(id, calculationData) {
		return this.request(`/leads/${id}/calculate-auto-quantities`, {
			method: 'POST',
			body: JSON.stringify(calculationData),
		});
	}

	async deleteLead(id) {
		return this.request(`/leads/${id}`, {
			method: 'DELETE',
		});
	}

	async getMenus(params = {}) {
		const qs = new URLSearchParams(params).toString();
		return this.request(`/menus${qs ? `?${qs}` : ''}`);
	}

	async getLead(leadId) {
		return this.request(`/leads/${leadId}`);
	}

	async getLeadMessages(leadId) {
		return this.request(`/leads/${leadId}/messages`);
	}

	async sendLeadMessage(leadId, messageData) {
		return this.request(`/leads/${leadId}/messages`, {
			method: 'POST',
			body: JSON.stringify(messageData),
		});
	}

	async markLeadMessagesAsRead(leadId) {
		return this.request(`/leads/${leadId}/messages/read`, {
			method: 'POST',
		});
	}

	// Wishlist endpoints
	async getWishlist() {
		return this.request('/wishlist');
	}

	async addToWishlist(menuId) {
		return this.request('/wishlist', {
			method: 'POST',
			body: JSON.stringify({ menu_id: menuId }),
		});
	}

	async removeFromWishlist(menuId) {
		return this.request(`/wishlist/${menuId}`, {
			method: 'DELETE',
		});
	}

	async updateWishlist(menuIds) {
		return this.request('/wishlist/bulk', {
			method: 'PUT',
			body: JSON.stringify({ menu_ids: menuIds }),
		});
	}

	// Vendor Wishlist endpoints
	async getVendorWishlist() {
		return this.request('/vendor-wishlist');
	}

	async addToVendorWishlist(vendorId) {
		return this.request('/vendor-wishlist', {
			method: 'POST',
			body: JSON.stringify({ vendor_id: vendorId }),
		});
	}

	async removeFromVendorWishlist(vendorId) {
		return this.request(`/vendor-wishlist/${vendorId}`, {
			method: 'DELETE',
		});
	}

	async updateVendorWishlist(vendorIds) {
		return this.request('/vendor-wishlist/bulk', {
			method: 'PUT',
			body: JSON.stringify({ vendor_ids: vendorIds }),
		});
	}

	// Admin endpoints
	async adminListVendors(params = {}) {
		const qs = new URLSearchParams(params).toString();
		return this.request(`/admin/vendors${qs ? `?${qs}` : ''}`);
	}

	// Admin MailerSend
	async adminMailTemplates(page = 1, limit = 25) {
		return this.request(`/admin/mail/templates?page=${page}&limit=${limit}`);
	}

	async adminSendMailTest(payload) {
		return this.request('/admin/mail/send-test', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json', ...this.getHeaders() },
			body: JSON.stringify(payload)
		});
	}

	// Admin SMTP (own server)
	async adminSmtpHealth() {
		return this.request('/admin/smtp/health');
	}

	async adminSmtpSendTest(payload) {
		return this.request('/admin/smtp/send-test', {
			method: 'POST',
			body: JSON.stringify(payload)
		});
	}

	// Admin Users
	async adminListUsers(params = {}) {
		const qs = new URLSearchParams(params).toString();
		return this.request(`/admin/users${qs ? `?${qs}` : ''}`);
	}
	async adminUpdateUser(id, payload) {
		return this.request(`/admin/users/${id}`, { method: 'PUT', body: JSON.stringify(payload) });
	}
	async adminDeleteUser(id) {
		return this.request(`/admin/users/${id}`, { method: 'DELETE' });
	}

	async adminVerifyVendor(id) {
		return this.request(`/admin/vendors/${id}/verify`, { method: 'POST' });
	}

	async adminRejectVendor(id) {
		return this.request(`/admin/vendors/${id}/reject`, { method: 'POST' });
	}

	async adminUpdateVendor(id, payload) {
		return this.request(`/admin/vendors/${id}`, {
			method: 'PUT',
			body: JSON.stringify(payload),
		});
	}

	async adminCreateVendor(payload) {
		return this.request('/admin/vendors', {
			method: 'POST',
			body: JSON.stringify(payload),
		});
	}

	async adminDeleteVendor(id) {
		return this.request(`/admin/vendors/${id}`, { method: 'DELETE' });
	}

	// Admin Media Library
	async adminGetMedia(params = {}) {
		const qs = new URLSearchParams(params).toString();
		return this.request(`/admin/media${qs ? `?${qs}` : ''}`);
	}

	async adminListBookings(params = {}) {
		const qs = new URLSearchParams(params).toString();
		return this.request(`/admin/bookings${qs ? `?${qs}` : ''}`);
	}

	async adminGetLeads(params = {}) {
		const qs = new URLSearchParams(params).toString();
		return this.request(`/admin/leads${qs ? `?${qs}` : ''}`);
	}

	async adminUpdateLead(id, leadData) {
		return this.request(`/admin/leads/${id}`, {
			method: 'PUT',
			body: JSON.stringify(leadData),
		});
	}
	async adminListMenus(params = {}) {
		const qs = new URLSearchParams(params).toString();
		return this.request(`/admin/menus${qs ? `?${qs}` : ''}`);
	}
	async adminUpdateMenu(id, payload) {
		return this.request(`/admin/menus/${id}`, { method: 'PUT', body: JSON.stringify(payload) });
	}
	async adminUpdateMenuWithFiles(id, formData) {
		return this.request(`/admin/menus/${id}`, { 
			method: 'POST', 
			body: formData,
			headers: {
				// Don't set Content-Type, let browser set it with boundary for FormData
				'X-Requested-With': 'XMLHttpRequest'
			}
		});
	}
	async adminApproveMenu(id) {
		return this.request(`/admin/menus/${id}/approve`, { method: 'POST' });
	}
	async adminRejectMenu(id) {
		return this.request(`/admin/menus/${id}/reject`, { method: 'POST' });
	}
	async adminDeleteMenu(id) {
		return this.request(`/admin/menus/${id}`, { method: 'DELETE' });
	}

	// Admin CMS: Cities
	async adminGetCities(params = {}) {
		const qs = new URLSearchParams(params).toString();
		return this.request(`/admin/cities${qs ? `?${qs}` : ''}`);
	}
	async adminCreateCity(payload) {
		return this.request('/admin/cities', { method: 'POST', body: JSON.stringify(payload) });
	}
	async adminUpdateCity(id, payload) {
		return this.request(`/admin/cities/${id}`, { method: 'PUT', body: JSON.stringify(payload) });
	}
	async adminDeleteCity(id) {
		return this.request(`/admin/cities/${id}`, { method: 'DELETE' });
	}

	// Admin CMS: Categories
	async adminGetCategories(params = {}) {
		const qs = new URLSearchParams(params).toString();
		return this.request(`/admin/categories${qs ? `?${qs}` : ''}`);
	}
	async adminCreateCategory(payload) {
		return this.request('/admin/categories', { method: 'POST', body: JSON.stringify(payload) });
	}
	async adminUpdateCategory(id, payload) {
		return this.request(`/admin/categories/${id}`, { method: 'PUT', body: JSON.stringify(payload) });
	}
	async adminDeleteCategory(id) {
		return this.request(`/admin/categories/${id}`, { method: 'DELETE' });
	}

	// Admin CMS: Packages
	async adminGetPackages(params = {}) {
		const qs = new URLSearchParams(params).toString();
		return this.request(`/admin/packages${qs ? `?${qs}` : ''}`);
	}
	async adminCreatePackage(payload) {
		return this.request('/admin/packages', { method: 'POST', body: JSON.stringify(payload) });
	}
	async adminUpdatePackage(id, payload) {
		return this.request(`/admin/packages/${id}`, { method: 'PUT', body: JSON.stringify(payload) });
	}
	async adminDeletePackage(id) {
		return this.request(`/admin/packages/${id}`, { method: 'DELETE' });
	}

	// Admin Integrations
	async adminGetIntegrations() {
		return this.request('/admin/integrations');
	}
	async adminUpdateIntegration(id, payload) {
		return this.request(`/admin/integrations/${id}`, { method: 'PUT', body: JSON.stringify(payload) });
	}
	async adminToggleIntegration(id, status) {
		return this.request(`/admin/integrations/${id}/toggle`, { method: 'PATCH', body: JSON.stringify({ status }) });
	}
	async adminTestIntegration(id) {
		return this.request(`/admin/integrations/${id}/test`, { method: 'POST' });
	}
	async adminConfigureIntegration(id, config) {
		return this.request(`/admin/integrations/${id}/configure`, { method: 'POST', body: JSON.stringify(config) });
	}

	// Admin Content Management
	async adminGetContent(params = {}) {
		const qs = new URLSearchParams(params).toString();
		return this.request(`/admin/content${qs ? `?${qs}` : ''}`);
	}

	async sendContactMessage(payload) {
		return this.request('/contact', { method: 'POST', body: JSON.stringify(payload) });
	}
	async adminGetContentByKey(key) {
		return this.request(`/admin/content/${key}`);
	}
	async adminCreateContent(payload) {
		return this.request('/admin/content', { method: 'POST', body: JSON.stringify(payload) });
	}
	async adminUpdateContent(id, payload) {
		return this.request(`/admin/content/${id}`, { method: 'PUT', body: JSON.stringify(payload) });
	}
	async adminDeleteContent(id) {
		return this.request(`/admin/content/${id}`, { method: 'DELETE' });
	}
	async adminBulkUpdateContent(payload) {
		return this.request('/admin/content/bulk-update', { method: 'POST', body: JSON.stringify(payload) });
	}

	// Admin Media Management
	async adminGetMedia(params = {}) {
		const qs = new URLSearchParams(params).toString();
		return this.request(`/admin/media${qs ? `?${qs}` : ''}`);
	}

	async adminGetActivityLogs(params = {}) {
		const qs = new URLSearchParams(params).toString();
		return this.request(`/admin/activity-logs${qs ? `?${qs}` : ''}`);
	}

	async adminGetSystemStats() {
		return this.request('/admin/stats');
	}

	async adminGetRecentActivity() {
		return this.request('/admin/recent-activity');
	}

	async adminTestUpload(formData) {
		const url = buildUrl('/admin/media/test-upload');
		const headers = {
			'Accept': 'application/json',
		};
		if (this.token) headers['Authorization'] = `Bearer ${this.token}`;
		// Don't set Content-Type for FormData - let browser set it with boundary
		
		const res = await fetch(url, { method: 'POST', headers, body: formData });
		const data = await res.json();
		
		if (!res.ok) {
			throw new Error(data.message || 'Test upload failed');
		}
		return data;
	}

	async adminUploadMedia(formData) {
		const url = buildUrl('/admin/media/upload');
		const headers = {
			'Accept': 'application/json',
		};
		if (this.token) headers['Authorization'] = `Bearer ${this.token}`;
		// Don't set Content-Type for FormData - let browser set it with boundary
		
		const res = await fetch(url, { method: 'POST', headers, body: formData });
		const data = await res.json();
		
		if (!res.ok) {
			throw new Error(data.message || 'Upload failed');
		}
		return data;
	}
	async adminDeleteMedia(id) {
		return this.request(`/admin/media/${id}`, { method: 'DELETE' });
	}

	// Public Content API
	async getContentBySection(section) {
		return this.request(`/content/section/${section}`);
	}

	async getPublicMediaByCategory(category) {
		return this.request(`/content/media/${encodeURIComponent(category)}`);
	}

	async getHeroSlides() {
		return this.request('/content/hero-slides');
	}

	async getPublicSettings() {
		return this.request('/content/settings');
	}

	async getPublicStats() {
		return this.request('/content/stats');
	}

	async getContentByKey(key) {
		return this.request(`/content/${key}`);
	}

	// Admin Email Configuration
	async adminGetEmailConfigurations() {
		return this.request('/admin/email-configurations');
	}

	async adminGetEmailConfiguration(id) {
		return this.request(`/admin/email-configurations/${id}`);
	}

	async adminCreateEmailConfiguration(payload) {
		return this.request('/admin/email-configurations', {
			method: 'POST',
			body: JSON.stringify(payload),
		});
	}

	async adminUpdateEmailConfiguration(id, payload) {
		return this.request(`/admin/email-configurations/${id}`, {
			method: 'PUT',
			body: JSON.stringify(payload),
		});
	}

	async adminDeleteEmailConfiguration(id) {
		return this.request(`/admin/email-configurations/${id}`, { method: 'DELETE' });
	}

	async adminTestEmailConfiguration(id) {
		return this.request(`/admin/email-configurations/${id}/test`, { method: 'POST' });
	}

	async adminSetDefaultEmailConfiguration(id) {
		return this.request(`/admin/email-configurations/${id}/set-default`, { method: 'POST' });
	}

	async adminGetEmailDrivers() {
		return this.request('/admin/email-configurations/drivers');
	}

	async adminGetEmailTemplates() {
		return this.request('/admin/email-configurations/templates');
	}

	// Admin Email Testing
	async adminSendTestEmail(testData) {
		return this.request('/admin/email-testing/send', {
			method: 'POST',
			body: JSON.stringify(testData),
		});
	}

	async adminGetEmailTestResults(params = {}) {
		const queryString = new URLSearchParams(params).toString();
		return this.request(`/admin/email-testing/results${queryString ? `?${queryString}` : ''}`);
	}

	async adminGetEmailTestStats() {
		return this.request('/admin/email-testing/stats');
	}

	async adminBulkTestEmailConfigurations() {
		return this.request('/admin/email-testing/bulk-test', { method: 'POST' });
	}

	async adminGetEmailTestHistory(configId) {
		return this.request(`/admin/email-testing/history/${configId}`);
	}

	// Admin Integrations
	async adminGetIntegrations(params = {}) {
		const queryString = new URLSearchParams(params).toString();
		return this.request(`/admin/integrations${queryString ? `?${queryString}` : ''}`);
	}

	async adminGetIntegration(id) {
		return this.request(`/admin/integrations/${id}`);
	}

	async adminCreateIntegration(payload) {
		return this.request('/admin/integrations', {
			method: 'POST',
			body: JSON.stringify(payload),
		});
	}

	async adminUpdateIntegration(id, payload) {
		return this.request(`/admin/integrations/${id}`, {
			method: 'PUT',
			body: JSON.stringify(payload),
		});
	}

	async adminDeleteIntegration(id) {
		return this.request(`/admin/integrations/${id}`, { method: 'DELETE' });
	}

	async adminTestIntegration(id) {
		return this.request(`/admin/integrations/${id}/test`, { method: 'POST' });
	}

	async adminToggleIntegration(id) {
		return this.request(`/admin/integrations/${id}/toggle`, { method: 'POST' });
	}

	async adminGetIntegrationTypes() {
		return this.request('/admin/integrations/types');
	}

	async adminGetIntegrationStats() {
		return this.request('/admin/integrations/stats');
	}

	// Admin Notifications
	async adminGetNotifications(params = {}) {
		const queryString = new URLSearchParams(params).toString();
		return this.request(`/admin/notifications${queryString ? `?${queryString}` : ''}`);
	}

	async adminGetNotification(id) {
		return this.request(`/admin/notifications/${id}`);
	}

	async adminCreateNotification(payload) {
		return this.request('/admin/notifications', {
			method: 'POST',
			body: JSON.stringify(payload),
		});
	}

	async adminUpdateNotification(id, payload) {
		return this.request(`/admin/notifications/${id}`, {
			method: 'PUT',
			body: JSON.stringify(payload),
		});
	}

	async adminDeleteNotification(id) {
		return this.request(`/admin/notifications/${id}`, { method: 'DELETE' });
	}

	async adminSendNotification(id) {
		return this.request(`/admin/notifications/${id}/send`, { method: 'POST' });
	}

	async adminRetryNotification(id) {
		return this.request(`/admin/notifications/${id}/retry`, { method: 'POST' });
	}

	async adminCancelNotification(id) {
		return this.request(`/admin/notifications/${id}/cancel`, { method: 'POST' });
	}

	async adminProcessScheduledNotifications() {
		return this.request('/admin/notifications/process-scheduled', { method: 'POST' });
	}

	async adminGetNotificationStats() {
		return this.request('/admin/notifications/stats');
	}

	// Admin System Logs
	async adminGetSystemLogs(params = {}) {
		const queryString = new URLSearchParams(params).toString();
		return this.request(`/admin/system-logs${queryString ? `?${queryString}` : ''}`);
	}

	async adminGetSystemLog(id) {
		return this.request(`/admin/system-logs/${id}`);
	}

	async adminCreateSystemLog(payload) {
		return this.request('/admin/system-logs', {
			method: 'POST',
			body: JSON.stringify(payload),
		});
	}

	async adminUpdateSystemLog(id, payload) {
		return this.request(`/admin/system-logs/${id}`, {
			method: 'PUT',
			body: JSON.stringify(payload),
		});
	}

	async adminDeleteSystemLog(id) {
		return this.request(`/admin/system-logs/${id}`, { method: 'DELETE' });
	}

	async adminGetSystemLogStats() {
		return this.request('/admin/system-logs/stats');
	}

	async adminCleanupSystemLogs(days = 30) {
		return this.request('/admin/system-logs/cleanup', {
			method: 'POST',
			body: JSON.stringify({ days }),
		});
	}

	// Admin Backups
	async adminGetBackups(params = {}) {
		const queryString = new URLSearchParams(params).toString();
		return this.request(`/admin/backups${queryString ? `?${queryString}` : ''}`);
	}

	async adminGetBackup(id) {
		return this.request(`/admin/backups/${id}`);
	}

	async adminCreateBackup(payload) {
		return this.request('/admin/backups', {
			method: 'POST',
			body: JSON.stringify(payload),
		});
	}

	async adminUpdateBackup(id, payload) {
		return this.request(`/admin/backups/${id}`, {
			method: 'PUT',
			body: JSON.stringify(payload),
		});
	}

	async adminDeleteBackup(id) {
		return this.request(`/admin/backups/${id}`, { method: 'DELETE' });
	}

	async adminStartBackup(payload) {
		return this.request('/admin/backups/create', {
			method: 'POST',
			body: JSON.stringify(payload),
		});
	}

	async adminDownloadBackup(id) {
		return this.request(`/admin/backups/${id}/download`, { method: 'POST' });
	}

	async adminRestoreBackup(id) {
		return this.request(`/admin/backups/${id}/restore`, { method: 'POST' });
	}

	async adminCleanupBackups() {
		return this.request('/admin/backups/cleanup', { method: 'POST' });
	}

	async adminGetBackupStats() {
		return this.request('/admin/backups/stats');
	}

	// Homepage content endpoints
	async getFeaturedTestimonials(limit = 3) {
		return this.request(`/testimonials/featured?limit=${limit}`);
	}

	async getActiveTestimonials(limit = null) {
		const params = limit ? `?limit=${limit}` : '';
		return this.request(`/testimonials/active${params}`);
	}

	async getHomepageFeatures(limit = 4) {
		return this.request(`/features/homepage?limit=${limit}`);
	}

	async getFeaturesByCategory(category, limit = null) {
		const params = limit ? `?limit=${limit}` : '';
		return this.request(`/features/category/${category}${params}`);
	}

	async getFeaturedArticles(limit = 3) {
		return this.request(`/articles/featured?limit=${limit}`);
	}

	async getLatestArticles(category = null, limit = 3) {
		const params = new URLSearchParams();
		if (category) params.append('category', category);
		if (limit) params.append('limit', limit);
		const queryString = params.toString();
		return this.request(`/articles/latest${queryString ? `?${queryString}` : ''}`);
	}

	async getArticlesByCategory(category, limit = null) {
		const params = limit ? `?limit=${limit}` : '';
		return this.request(`/articles/category/${category}${params}`);
	}

	async getArticle(slug) {
		return this.request(`/articles/${slug}`);
	}

	// Admin content management endpoints
	async adminGetTestimonials(params = {}) {
		const queryString = new URLSearchParams(params).toString();
		return this.request(`/admin/testimonials${queryString ? `?${queryString}` : ''}`);
	}

	async adminCreateTestimonial(data) {
		return this.request('/admin/testimonials', {
			method: 'POST',
			body: JSON.stringify(data),
		});
	}

	async adminUpdateTestimonial(id, data) {
		return this.request(`/admin/testimonials/${id}`, {
			method: 'PUT',
			body: JSON.stringify(data),
		});
	}

	async adminDeleteTestimonial(id) {
		return this.request(`/admin/testimonials/${id}`, { method: 'DELETE' });
	}

	async adminGetFeatures(params = {}) {
		const queryString = new URLSearchParams(params).toString();
		return this.request(`/admin/features${queryString ? `?${queryString}` : ''}`);
	}

	async adminCreateFeature(data) {
		return this.request('/admin/features', {
			method: 'POST',
			body: JSON.stringify(data),
		});
	}

	async adminUpdateFeature(id, data) {
		return this.request(`/admin/features/${id}`, {
			method: 'PUT',
			body: JSON.stringify(data),
		});
	}

	async adminDeleteFeature(id) {
		return this.request(`/admin/features/${id}`, { method: 'DELETE' });
	}

	async adminGetArticles(params = {}) {
		const queryString = new URLSearchParams(params).toString();
		return this.request(`/admin/articles${queryString ? `?${queryString}` : ''}`);
	}

	async adminCreateArticle(data) {
		return this.request('/admin/articles', {
			method: 'POST',
			body: JSON.stringify(data),
		});
	}

	async adminUpdateArticle(id, data) {
		return this.request(`/admin/articles/${id}`, {
			method: 'PUT',
			body: JSON.stringify(data),
		});
	}

	async adminDeleteArticle(id) {
		return this.request(`/admin/articles/${id}`, { method: 'DELETE' });
	}

	// Payment Configuration
	async adminGetPaymentSettings() {
		return this.request('/admin/payment-settings');
	}

	async adminUpdatePaymentSettings(payload) {
		return this.request('/admin/payment-settings', {
			method: 'PUT',
			body: JSON.stringify(payload),
		});
	}

	async adminTestPaymentProvider(provider) {
		return this.request(`/admin/payment-settings/test/${provider}`, { method: 'POST' });
	}

	async adminGetPaymentStats() {
		return this.request('/admin/payment-settings/stats');
	}

	async adminGetPaymentTransactions(params = {}) {
		const queryString = new URLSearchParams(params).toString();
		return this.request(`/admin/payment-settings/transactions${queryString ? `?${queryString}` : ''}`);
	}

	// Generic HTTP methods
	async get(endpoint) {
		return this.request(endpoint);
	}

	async post(endpoint, data = {}) {
		return this.request(endpoint, {
			method: 'POST',
			body: JSON.stringify(data),
		});
	}

	async put(endpoint, data = {}) {
		return this.request(endpoint, {
			method: 'PUT',
			body: JSON.stringify(data),
		});
	}

	async delete(endpoint) {
		return this.request(endpoint, { method: 'DELETE' });
	}

	// Notifications endpoints
	async getNotifications(params = {}) {
		const queryString = new URLSearchParams(params).toString();
		return this.request(`/notifications${queryString ? `?${queryString}` : ''}`);
	}

	async markNotificationAsRead(id) {
		return this.request(`/notifications/${id}/read`, { method: 'POST' });
	}

	async markAllNotificationsAsRead() {
		return this.request('/notifications/mark-all-read', { method: 'POST' });
	}

	async deleteNotification(id) {
		return this.request(`/notifications/${id}`, { method: 'DELETE' });
	}

	// Location search endpoints
	async searchLocations(query, params = {}) {
		const searchParams = new URLSearchParams({ q: query, ...params });
		return this.request(`/geo/search?${searchParams.toString()}`);
	}

	// Payment Schedule endpoints
	async getPaymentSchedule(bookingId) {
		return this.request(`/bookings/${bookingId}/payment-schedule`);
	}

	async updatePaymentSchedule(bookingId, scheduleData) {
		return this.request(`/bookings/${bookingId}/payment-schedule`, {
			method: 'PUT',
			body: JSON.stringify(scheduleData),
		});
	}

	async requestAdvancePayment(bookingId, advanceData) {
		return this.request(`/bookings/${bookingId}/advance-payment`, {
			method: 'POST',
			body: JSON.stringify(advanceData),
		});
	}

	async payMilestone(bookingId, milestoneId) {
		return this.request(`/bookings/${bookingId}/milestones/${milestoneId}/pay`, {
			method: 'POST',
		});
	}

	async getBookingReviews(bookingId) {
		return this.request(`/bookings/${bookingId}/reviews`);
	}

	async createBookingReview(bookingId, reviewData) {
		return this.request(`/bookings/${bookingId}/reviews`, {
			method: 'POST',
			body: JSON.stringify(reviewData),
		});
	}

	// Quote Payment Methods
	async createQuotePaymentLink(quoteId, paymentData) {
		return this.request(`/quotes/${quoteId}/payment-links`, {
			method: 'POST',
			body: JSON.stringify(paymentData),
		});
	}

	async getQuotePaymentLinks(quoteId) {
		return this.request(`/quotes/${quoteId}/payment-links`);
	}

	async createQuotePaymentSchedule(quoteId, scheduleData) {
		return this.request(`/quotes/${quoteId}/payment-schedule`, {
			method: 'POST',
			body: JSON.stringify(scheduleData),
		});
	}

	async getQuotePaymentSchedule(quoteId) {
		return this.request(`/quotes/${quoteId}/payment-schedule`);
	}

	async processQuotePayment(quoteId, paymentData) {
		return this.request(`/quotes/${quoteId}/process-payment`, {
			method: 'POST',
			body: JSON.stringify(paymentData),
		});
	}

	// Lead management
	async createLead(leadData) {
		return this.request('/leads', {
			method: 'POST',
			body: JSON.stringify(leadData),
		});
	}

	async getLeads(params = {}) {
		const queryString = new URLSearchParams(params).toString();
		return this.request(`/leads${queryString ? `?${queryString}` : ''}`);
	}

	// Email services
	async sendEmail(emailData) {
		return this.request('/email/send', {
			method: 'POST',
			body: JSON.stringify(emailData),
		});
	}

	async sendOrderEmail(orderData) {
		return this.request('/email/order-request', {
			method: 'POST',
			body: JSON.stringify(orderData),
		});
	}
}

export default new ApiService();
